<script setup lang="ts">
// eslint-disable-next-line regex/invalid
import { NuxtLink } from '#components';
import type { StoryblokMaybeLivePreview } from '#shared/types/storyblok.type';
import { INJECTION_KEYS } from '~/constants/injectionKeys';

// PROPS
export interface StoryblokBlogPostProps {
    blok: StoryblokMaybeLivePreview<StoryblokBlogPost>;
}

const { blok } = defineProps<StoryblokBlogPostProps>();

// COMPOSABLES
const { td } = useTypedI18n();

// INJECT
const storyMeta = inject(INJECTION_KEYS.STORYBLOK_STORY_META);
const defaultFullSlug = computed(() => storyMeta?.value?.default_full_slug ?? undefined);
const publishedAt = computed<string | undefined>(
    () => storyMeta?.value?.first_published_at ?? storyMeta?.value?.updated_at ?? storyMeta?.value?.created_at ?? undefined
);

// DATA
const publicationDate = computed(() => (publishedAt.value ? new Date(Date.parse(publishedAt.value)) : undefined));
const surround = await useStoryblokBlogSurround(defaultFullSlug, publishedAt);
const breadcrumbs = await useStoryblokBlogBreadcrumbs(defaultFullSlug, blok.title);
const tocLinks = useStoryblokBlogToc(() => blok.body);

// SEO
useSchemaOrg([
    defineArticle({
        '@type': 'BlogPosting',
        // name and description can usually be inferred
        image: blok.cover_image.filename,
        datePublished: publicationDate.value,
    }),
    defineBreadcrumb({
        itemListElement: breadcrumbs.value.map((b, i) => ({ name: b.label, item: String(b.to), position: i + 1 })),
    }),
]);
</script>

<template>
    <div v-editable="blok">
        <div class="relative">
            <NuxtImg
                format="webp"
                class="absolute -z-20 h-full w-full object-cover"
                draggable="false"
                :provider="storyblokImageProvider(blok.cover_image)"
                :src="blok.cover_image.filename"
                :alt="blok.cover_image.alt ?? ''"
            />
            <div class="absolute -z-10 h-full w-full bg-linear-to-t from-neutral-950 to-white opacity-50"></div>
            <BaseWidthContainer class="pt-50">
                <UPageHeader
                    :title="blok.title"
                    :description="blok.excerpt"
                    :ui="{
                        root: 'border-0',
                        title: 'text-inverted',
                        description: 'text-inverted/80',
                        headline: 'flex-col items-start justify-start',
                    }"
                >
                    <template #headline>
                        <div class="flex flex-row gap-x-2">
                            <template v-if="blok.categories?.length">
                                <UBadge
                                    v-for="(category, i) in blok.categories"
                                    :key="i"
                                    :as="NuxtLink"
                                    :to="storyblokGetStoryUrl(category)"
                                    color="neutral"
                                    variant="subtle"
                                    :label="category.content.title"
                                />
                            </template>
                            <span class="text-inverted/80">&middot;</span>

                            <time v-if="publicationDate" class="text-inverted/80">
                                <ClientOnly>{{ td(publicationDate, 'ddmmyyyy') }}</ClientOnly>
                            </time>
                        </div>
                        <div class="text-inverted/80 mt-2">
                            <BaseBreadcrumb color="inherit" :items="breadcrumbs" />
                        </div>
                    </template>
                </UPageHeader>
            </BaseWidthContainer>
        </div>
        <BaseWidthContainer>
            <UPage>
                <UPageBody>
                    <StoryblokRichTextOverride :doc="blok.body" class="max-w-none" />

                    <USeparator v-if="surround?.length" />

                    <UContentSurround :surround="surround" />
                </UPageBody>
                <template v-if="tocLinks?.length" #right>
                    <UContentToc :links="tocLinks" />
                </template>
            </UPage>
        </BaseWidthContainer>
    </div>
</template>
